import { ReactNode } from 'react';
import AffinityLogo from '../../../assets/icons/affinity.svg?react';
import AirtableLogo from '../../../assets/icons/airtable.svg?react';
import AllVueLogo from '../../../assets/icons/allvue.svg?react';
import ArkPesLogoUrl from '../../../assets/icons/arkpes.png';
import CartaLogo from '../../../assets/icons/carta.svg?react';
import CobaltLogo from '../../../assets/icons/cobalt.svg?react';
import DropboxLogo from '../../../assets/icons/dropbox.svg?react';
import GoogleDriveLogo from '../../../assets/icons/google-drive.svg?react';
import ILevelLogo from '../../../assets/icons/ilevel.svg?react';
import StandardMetricsLogo from '../../../assets/icons/standard-metrics.svg?react';
import QuickbooksLogo from '../../../assets/icons/quickbooks.svg?react';
import SalesforceLogo from '../../../assets/icons/salesforce.svg?react';
import SlackLogo from '../../../assets/icons/slack.svg?react';
import XeroLogo from '../../../assets/icons/xero-integration.svg?react';
import { AppName, IIntegrationsAppDataModel } from '../../../data-models/integrations-app.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';

export const AppMap = new Map<
  AppName,
  { displayName: string; icon: ReactNode; description: string; routeKey: string }
>();

AppMap.set(AppName.Affinity, {
  displayName: 'Affinity',
  routeKey: 'affinity',
  description: 'Relationship intelligence platform for managing deal flow and investor networks.',
  icon: <AffinityLogo />,
});
AppMap.set(AppName.AirTable, {
  displayName: 'Airtable',
  routeKey: 'airtable',
  description: 'Flexible database for organizing workflows, tracking deals, and managing structured data.',
  icon: <AirtableLogo />,
});
AppMap.set(AppName.AllVue, {
  displayName: 'AllVue',
  routeKey: 'allvue',
  description: 'Investment management platform for fund accounting, portfolio monitoring, and reporting.',
  icon: <AllVueLogo />,
});
AppMap.set(AppName.ArkPes, {
  displayName: 'Ark PES',
  routeKey: 'arkpes',
  description: 'Private equity services platform for fund administration and investor reporting.',
  icon: (
    <img src={ArkPesLogoUrl} alt='Ark PES' style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
  ),
});
AppMap.set(AppName.ILevel, {
  displayName: 'iLevel',
  routeKey: 'ilevel',
  description: 'Portfolio monitoring solution for tracking performance across private market investments.',
  icon: <ILevelLogo />,
});
AppMap.set(AppName.StandardMetrics, {
  displayName: 'Standard Metrics',
  routeKey: 'standard-metrics',
  description: 'Portfolio data platform for collecting, analyzing, and benchmarking company performance.',
  icon: <StandardMetricsLogo />,
});
AppMap.set(AppName.Cobalt, {
  displayName: 'Cobalt',
  routeKey: 'cobalt',
  description: 'Portfolio monitoring platform for collecting and reporting company financial metrics.',
  icon: <CobaltLogo />,
});
AppMap.set(AppName.Carta, {
  displayName: 'Carta',
  routeKey: 'carta',
  description: 'Equity management platform for cap tables, valuations, and investor reporting.',
  icon: <CartaLogo />,
});
AppMap.set(AppName.Dropbox, {
  displayName: 'Dropbox',
  routeKey: 'dropbox',
  description: 'Secure file storage and sharing for team documents, reports, and deal materials.',
  icon: <DropboxLogo />,
});
AppMap.set(AppName.GoogleDrive, {
  displayName: 'Google Drive',
  routeKey: 'google-drive',
  description: 'Cloud workspace for storing, collaborating, and accessing documents across teams.',
  icon: <GoogleDriveLogo />,
});
AppMap.set(AppName.QuickBooks, {
  displayName: 'QuickBooks',
  routeKey: 'quickbooks',
  description: 'Cloud-based accounting software for managing financials, invoicing, and reporting.',
  icon: <QuickbooksLogo />,
});
AppMap.set(AppName.Salesforce, {
  displayName: 'Salesforce',
  routeKey: 'salesforce',
  description: 'CRM platform for managing pipelines, tracking relationships, and closing deals efficiently.',
  icon: <SalesforceLogo />,
});
AppMap.set(AppName.Slack, {
  displayName: 'Slack',
  routeKey: 'slack',
  description: 'Team communication platform for real-time collaboration and operational coordination.',
  icon: <SlackLogo />,
});
AppMap.set(AppName.Xero, {
  displayName: 'Xero',
  routeKey: 'xero',
  description: 'Cloud accounting software for managing financials, expenses, and reporting.',
  icon: <XeroLogo />,
});

export const ConfigurableApps = new Set<AppName>([
  AppName.Affinity,
  AppName.AirTable,
  AppName.AllVue,
  AppName.ArkPes,
  AppName.ILevel,
  AppName.QuickBooks,
  AppName.StandardMetrics,
  AppName.Cobalt,
]);

/** Either key opens Carta V2 Manage; a Finance role's `fd.integrations.*` covers the first. */
export const CartaManagePermissions = [PermissionKey.canViewCartaManage, PermissionKey.customerSuccessAll];

export function isAppConfigurable(app: IIntegrationsAppDataModel): boolean {
  return (
    ConfigurableApps.has(app.appName) ||
    (app.appName === AppName.Carta &&
      app.cartaV2 === true &&
      CartaManagePermissions.some((permission) => PermissionService.get().hasPermission(permission)))
  );
}

export function canViewApp(appName: AppName) {
  switch (appName) {
    case AppName.Affinity:
    case AppName.AirTable:
    case AppName.AllVue:
    case AppName.ArkPes:
    case AppName.Carta:
    case AppName.Dropbox:
    case AppName.GoogleDrive:
    case AppName.ILevel:
    case AppName.StandardMetrics:
    case AppName.Cobalt:
    case AppName.QuickBooks:
    case AppName.Salesforce:
    case AppName.Slack:
    case AppName.Xero:
      return PermissionService.get().hasPermission(PermissionKey.hasIntegrationsModule);
    default:
      return false;
  }
}
